import { Badge, Box, Button, Dialog, Flex, Heading, IconButton, Separator, Text } from '@radix-ui/themes';
import React, { useState } from 'react';
import { CgArrowsExpandUpRight } from 'react-icons/cg';
import { MdOutlineVerifiedUser } from 'react-icons/md';
import { RiDeleteBin5Fill } from 'react-icons/ri';
import { createPortal } from 'react-dom';
import ConfirmationDialogue from './ConfirmationDialogue';
import axiosInstance from '../../config/axiosConfig';

const ViewGateway = ({ initialValues = null, onGatewayUpdated, showToast, isAdmin = false }) => {
  const [isViewOpen, setViewOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationType, setConfirmationType] = useState(null);

  const handleConfirmation = (type) => {
    setViewOpen(false);
    setConfirmationType(type);
    setConfirmationOpen(true);
  };

  const confirmationHeadings = {
    Delete: 'Deletion',
    Activate: 'Activation',
    Deactivate: 'Deactivation',
  };

  const handleDelete = async () => {
    try {
      const endpoint = isAdmin 
        ? `/gateway/delete-institution-gateway/${initialValues.token}` 
        : `/gateway/delete-gateway/${initialValues.token}`;
      const response = await axiosInstance.post(endpoint);
      if (response.data.result) {
        if (onGatewayUpdated) {
          await onGatewayUpdated(isAdmin ? { token: initialValues.token, deleted: true } : undefined);
        }
        return true;
      } else {
        console.error('Delete failed:', response.data);
        if (showToast) showToast(response.data.errorResponse?.message || 'Failed to delete gateway', 'error');
        return false;
      }
    } catch (error) {
      console.error('Error deleting gateway:', error);
      if (showToast) showToast(error.response?.data?.errorResponse?.message || 'Error deleting gateway', 'error');
      return false;
    }
  };

  const handleUpdateStatus = async (isActive) => {
    try {
      const gatewayData = {
        token: initialValues.token,
        isActive: isActive,
        isSuperAdmin: !isAdmin,
      };

      const endpoint = isAdmin 
        ? '/gateway/update-institution-gateway' 
        : '/gateway/update-gateway';
      const response = await axiosInstance.post(endpoint, gatewayData);
      if (response.data.result) {
        if (onGatewayUpdated) {
          await onGatewayUpdated(isAdmin ? { token: initialValues.token, isActive } : undefined);
        }
        return true;
      } else {
        console.error('Update failed:', response.data);
        if (showToast) showToast(response.data.errorResponse?.message || 'Failed to update gateway status', 'error');
        return false;
      }
    } catch (error) {
      console.error('Error updating gateway:', error);
      if (showToast) showToast(error.response?.data?.errorResponse?.message || 'Error updating gateway', 'error');
      return false;
    }
  };

  const handleConfirmAction = async () => {
    let success = false;
    let actionMessage = '';
    if (confirmationType === 'Delete') {
      success = await handleDelete();
      actionMessage = 'Deleted';
    } else if (confirmationType === 'Activate') {
      success = await handleUpdateStatus(true);
      actionMessage = 'Activated';
    } else if (confirmationType === 'Deactivate') {
      success = await handleUpdateStatus(false);
      actionMessage = 'Deactivated';
    }

    if (success) {
      if (showToast) showToast(`${actionMessage} successfully`);
      setConfirmationOpen(false);
    } else {
      setConfirmationOpen(false);
      setViewOpen(true);
    }
  };

  const renderDetail = (label, value) => (
    <Box className="w-[48%] rounded border border-gray-200 px-3 py-2">
      <Heading size="1" color="gray">{label}</Heading>
      <Text size="2">{value || 'N/A'}</Text>
    </Box>
  );

  const dialogContent = isViewOpen ? (
    <Dialog.Content maxWidth="450px">
      <Flex direction="column" gap="4">

        {/* Header */}
        <Flex justify="between" align="start">
          <Flex direction="column">
            <Heading size="4">Gateway Details</Heading>
            <Flex direction="row" gap="2" align="center" mt="1">
              <Text size="1">Status</Text>
              <Separator orientation="vertical" />
              <Badge color={initialValues.isActive ? 'green' : 'tomato'}>
                <MdOutlineVerifiedUser />
                {initialValues.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </Flex>
          </Flex>
        </Flex>

        <Separator size="4" />

        {/* Detail cards */}
        <Flex gap="3" wrap="wrap">
          {!isAdmin && renderDetail('Gateway Address', initialValues.gateway_address)}
          {renderDetail('IGIN', initialValues.igin)}
          {renderDetail('IGVN', initialValues.igvn)}
        </Flex>

        {/* Action buttons */}
        <Flex direction="row" gap="2" align="center" justify="end" mt="3">
          <Button
            color={initialValues.isActive ? 'red' : 'green'}
            onClick={() => handleConfirmation(initialValues.isActive ? 'Deactivate' : 'Activate')}
          >
            {initialValues.isActive ? 'Deactivate' : 'Activate'}
          </Button>
          <Button color="gray" variant="soft" onClick={() => handleConfirmation('Delete')}>
            <RiDeleteBin5Fill /> Delete
          </Button>
        </Flex>

      </Flex>
    </Dialog.Content>
  ) : null;

  return (
    <>
      <Dialog.Root open={isViewOpen} onOpenChange={setViewOpen}>
        <Dialog.Trigger>
          <IconButton variant="solid" onClick={() => setViewOpen(true)}>
            <CgArrowsExpandUpRight width="18" height="18" />
          </IconButton>
        </Dialog.Trigger>
        {isViewOpen && createPortal(dialogContent, document.body)}
      </Dialog.Root>

      {confirmationOpen && (
        <ConfirmationDialogue
          Name={initialValues.igin}
          Type={confirmationHeadings[confirmationType] || 'Confirm Action'}
          Action={confirmationType.toLowerCase()}
          onCancel={() => {
            setConfirmationOpen(false);
            setViewOpen(true);
          }}
          onConfirm={handleConfirmAction}
        />
      )}
    </>
  );
};

export default ViewGateway;
